import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dealAPI, contentAPI, ideaAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals,   setDeals]   = useState([]);
  const [posts,   setPosts]   = useState([]);
  const [ideas,   setIdeas]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([dealAPI.getAll(), contentAPI.getAll(), ideaAPI.getAll()])
      .then(([d, c, i]) => { setDeals(d.data); setPosts(c.data); setIdeas(i.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const activeDeals    = deals.filter(d => !['PAID'].includes(d.status));
  const pipelineValue  = deals.filter(d => ['CONFIRMED','DELIVERED','PAID'].includes(d.status))
    .reduce((sum, d) => sum + (d.amount || 0), 0);
  const thisWeekPosts  = posts.filter(p => {
    if (!p.scheduledDate) return false;
    const d = new Date(p.scheduledDate);
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 7;
  });

  const stats = [
    { label: 'Active Deals',     value: activeDeals.length,           icon: '🤝', color: 'var(--blue)',   pale: 'var(--blue-pale)',   link: '/deals' },
    { label: 'Pipeline Value',   value: `₹${pipelineValue.toLocaleString('en-IN')}`, icon: '💰', color: 'var(--green)', pale: 'var(--green-pale)', link: '/deals' },
    { label: 'Posts This Week',  value: thisWeekPosts.length,         icon: '📅', color: 'var(--purple)', pale: 'var(--purple-pale)', link: '/calendar' },
    { label: 'Ideas Saved',      value: ideas.length,                 icon: '💡', color: 'var(--yellow)', pale: 'var(--yellow-pale)', link: '/ideas' },
  ];

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Loading dashboard...</div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Good morning, {user?.name?.split(' ')[0] || 'Creator'} 👋</div>
        <div className="page-subtitle">Here's what's happening with your creator business</div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        {stats.map(s => (
          <div key={s.label} className="card" onClick={() => navigate(s.link)}
            style={{ cursor: 'pointer', transition: 'all 0.15s' }}
            onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
              <div style={{ width: 36, height: 36, borderRadius: '10px', background: s.pale, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>{s.icon}</div>
            </div>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: '1.8rem', fontWeight: 800, color: 'var(--ink)' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Bottom grid */}
      <div className="grid-2">

        {/* Recent deals */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>🤝 Recent Brand Deals</div>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/deals')}>View all</button>
          </div>
          {activeDeals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
              No active deals yet.<br />
              <button className="btn btn-green btn-sm" style={{ marginTop: '0.75rem' }} onClick={() => navigate('/deals')}>+ Add first deal</button>
            </div>
          ) : (
            activeDeals.slice(0, 4).map(d => (
              <div key={d.id} onClick={() => navigate('/deals')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.65rem 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{d.brandName}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{d.platform} · {d.dealType}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {d.amount && <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--green)' }}>₹{Number(d.amount).toLocaleString('en-IN')}</div>}
                  <span className={`badge status-${d.status}`}>{d.status}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Upcoming posts */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>📅 Upcoming Content</div>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/calendar')}>View all</button>
          </div>
          {posts.filter(p => !['POSTED'].includes(p.status)).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)', fontSize: '0.85rem' }}>
              No content planned.<br />
              <button className="btn btn-green btn-sm" style={{ marginTop: '0.75rem' }} onClick={() => navigate('/calendar')}>+ Plan content</button>
            </div>
          ) : (
            posts.filter(p => !['POSTED'].includes(p.status)).slice(0, 4).map(p => (
              <div key={p.id} onClick={() => navigate('/calendar')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.65rem 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{p.title}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{p.platform} · {p.scheduledDate || 'No date set'}</div>
                </div>
                <span className={`badge status-${p.status}`}>{p.status}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
